/**
 * Project: Academy V2
 * File: InnerClassReport.tsx
 * Author: Colopi Salvatore
 * Author URI: https://salvatorecolopi.com
 * Created: Wed Jul 10 2024 at 20:34:36
 *
 * Copyright (c) 2024 Edusogno. All rights reserved.
 */

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/Avatar.tsx";
import { FeedbackDetail, NavigationState, UserReportData } from '@/types';
import { cn } from '@/utils/string';
import Button from '@edusogno/primitives/button.js';
import { ScrollArea } from '@edusogno/primitives/scroll-area.js';
import { t } from 'i18next';
import { enqueueSnackbar } from "notistack";
import React from 'react';
import { useLocation } from 'react-router-dom';
import MediaIcon from '../icons/MediaIcon';

const STORAGE_ROUTE = import.meta.env.EDS_STORAGE_URL;

// const Feedback: React.FC<FeedbackDetail> = ({ student_name, mistake, feedback, explanation }) => {
//   const details = [
//     mistake ? { label: 'Mistake', value: mistake } : null,
//     feedback ? { label: 'Feedback', value: feedback } : null,
//     explanation ? { label: 'Explanation', value: explanation } : null
//   ].filter(Boolean);

//   return (
//     <article className="mb-4 text-xs text-[--secondary]">
//       <p className="text-[--secondary]"><span className='italic text-[--primary]'>{student_name}</span>: "{feedback}"</p>
//       <div className="ml-4">
//         {details.map((detail, index) => (
//           <p key={index}>- {detail?.label}: {detail?.value}</p>
//         ))}
//       </div>
//     </article>
//   );
// };
interface GroupedFeedback {
  [studentName: string]: FeedbackDetail[];
}

const FeedbackSection: React.FC<{ title: string; feedbackItems: FeedbackDetail[] }> = ({ title, feedbackItems }) => {
  if (feedbackItems.length === 0) {
    return null;
  }

  const groupedFeedback = feedbackItems.reduce<GroupedFeedback>((acc, item) => {
    acc[item.student_name] = acc[item.student_name] || [];
    acc[item.student_name].push(item);
    return acc;
  }, {});

  return (
    <>
      <h2 className="font-bold text-xs text-[--secondary]">{title}</h2>
      {Object.entries(groupedFeedback).map(([studentName, items]) => (
        <article key={studentName} className="mb-4 text-xs text-[--secondary]">
          <p className="text-[--secondary]">
            <span className='italic text-[--primary]'>{studentName}</span>
          </p>
          {items.map((item, idx) => (
            <div className="ml-4" key={idx}>
              <p className="font-bold">- Feedback: {item.feedback}</p>
              <p>- Mistake: {item.mistake}</p>
              <p>- Explanation: {item.explanation}</p>
              <br />
            </div>
          ))}
        </article>
      ))}
    </>
  );
};

const InnerClassReport: React.FC<UserReportData> = ({
  grammar,
  vocabulary,
  pronunciation
}) => {
  const location = useLocation();
  const state = location.state as NavigationState;
  const { tutorPic, tutorName, tutorSurname, linkVideo } = state || {};

  const handleRedirect = () => {
    if (linkVideo) {
      window.open(linkVideo, "_blank");
    } else {
      enqueueSnackbar(t("class_report.no_recording"), { variant: "error" });
    }
  };

  return (
    <ScrollArea className="flex flex-col justify-start p-5 font-medium leading-4 bg-white rounded-2xl h-full max-md:max-w-full">
      <section className="z-10 p-2 bg-white max-md:max-w-full">
        <div className={"flex flex-row justify-between items-center gap-2 lg:hidden mb-3"}>
          <div className='flex flex-row items-center gap-2'>
            <Avatar className={"w-12 h-12"}>
              <AvatarImage
                src={`${STORAGE_ROUTE}/${tutorPic}`}
                className={"object-cover"}
              />
              <AvatarFallback>
                {(tutorName?.charAt(0) || '') + (tutorSurname?.charAt(0) || '')}
              </AvatarFallback>
            </Avatar>
            <p
              className={
                "lg:block text-xs font-medium text-center text-[--secondary]"
              }
            >
              {tutorName}
            </p>
          </div>
          {linkVideo && (
            <Button
              onClick={handleRedirect}
              variant={"ghost"}
              className={"w-fit lg:hidden"}
            >
              <MediaIcon className={cn("h-auto w-7 xl:hidden",)} />
            </Button>
          )}
        </div>
        <FeedbackSection title="GRAMMAR" feedbackItems={grammar || []} />
        <FeedbackSection title="VOCABULARY" feedbackItems={vocabulary || []} />
        <FeedbackSection title="PRONUNCIATION" feedbackItems={pronunciation || []} />
      </section>
    </ScrollArea>
  );
};

export default InnerClassReport;